/**
 * Live BTC price + ATH from CoinGecko's free public endpoint.
 *
 * Endpoint: https://api.coingecko.com/api/v3/coins/bitcoin
 *   Returns { market_data: { current_price: { usd }, ath: { usd } }, ... }
 *
 * No API key. Free tier rate-limited to ~30 calls/min but in practice the
 * shared-IP limits hit much sooner during traffic spikes (some users have
 * reported 429s after as few as 10 calls in tight succession).
 *
 * Patch CW: 8-second AbortController timeout per fetch. Mobile PWA cold
 * starts on slow networks sometimes leave fetches hanging for ~60s (the
 * browser default), which permanently blocked the retry chain in
 * useAutoRefresh. 8s is generous for a CoinGecko response that normally
 * returns in under 1s; anything longer is stuck and worth abandoning.
 *
 * Patch DS: module-level cache + in-flight deduplication. Solves two
 * bugs that DN/DP could not fix because they were at the wrong layer:
 *
 *   1. Rate-limit cascade. Dashboard mount fires fetchBtcUsd via
 *      useAutoRefresh. Switching tabs unmounts Dashboard, switching
 *      back remounts it - the per-mount lastFetchRef in useAutoRefresh
 *      resets to 0 every time, so minGapMs never applies across
 *      mounts and every page switch fires another CoinGecko call.
 *      Four or five switches in a minute is enough to hit a 429 from
 *      the free tier, after which every subsequent call also 429s
 *      because the limit window persists. The user is locked out for
 *      a minute or more with no way to recover except waiting.
 *      Caching successful snapshots for 60s means rapid switches reuse
 *      the recent value and never hit the API.
 *
 *   2. Stale-resolution race. When Dashboard unmounts mid-fetch, the
 *      in-flight Promise is NOT cancelled - React only cleans up the
 *      effect, the fetch keeps running and its setLiveStatus/
 *      setLiveSnapshot side effects fire when it eventually resolves.
 *      A remount fires a NEW fetch concurrently. Two writes to the
 *      store racing; last-writer-wins decides whether the user sees
 *      ok or error, irrespective of which call actually succeeded.
 *      Deduplicating via the inFlight promise means concurrent callers
 *      receive the SAME result, so they cannot disagree on what to
 *      write into the store.
 *
 * Both bugs were the actual root cause of the "live error stuck for
 * minutes" symptom that DN and DP tried to fix at the recovery layer.
 * DN and DP were solving the wrong problem - those patches handle iOS
 * PWA wake recovery, which is a real but different failure mode. DS
 * fixes the rate-limit-and-race failure mode that surfaces during
 * normal page-switching usage even without any iOS suspension.
 */
import { Decimal } from '../../engine/money.js';
import type { LiveBtcSnapshot, LiveDataProvider, LiveAaveRateSnapshot } from '../../engine/types.js';

const COINGECKO_URL = 'https://api.coingecko.com/api/v3/coins/bitcoin?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false';
const FETCH_TIMEOUT_MS = 8000;

// Patch DS: module-level cache TTL. 60s is the sweet spot: long enough
// to absorb rapid page switches (typically the user lands on Dashboard,
// peeks at Retire, comes back - all within a few seconds), short enough
// that price drift on a volatile asset never gets noticeably stale.
// The interval-based refresh in useAutoRefresh runs every 2 minutes
// anyway, so 60s of cache slots cleanly inside one interval cycle.
const CACHE_TTL_MS = 60_000;

// Patch DS: module-scoped cache + in-flight dedup state. Scoped to the
// module not the class instance so a fresh CoinGeckoProvider in a
// different component sees the same cache (the app instantiates one
// provider in App.tsx and passes it around, but defensive scoping is
// free here).
let cachedSnapshot: LiveBtcSnapshot | null = null;
let cachedAt = 0;
let inFlight: Promise<LiveBtcSnapshot> | null = null;

interface CoinGeckoResponse {
  market_data?: {
    current_price?: { usd?: number };
    ath?: { usd?: number };
  };
}

export class CoinGeckoProvider implements LiveDataProvider {
  async fetchBtcUsd(): Promise<LiveBtcSnapshot> {
    // Patch DS cache check. If we have a successful snapshot less than
    // CACHE_TTL_MS old, return it without hitting the network. The
    // snapshot retains its original fetchedAt so the UI "X minutes ago"
    // text correctly reflects when the data was actually fetched, not
    // when this cache hit happened.
    if (cachedSnapshot && Date.now() - cachedAt < CACHE_TTL_MS) {
      return cachedSnapshot;
    }
    // Patch DS in-flight dedup. If a fetch is already running, all
    // additional callers await the same promise. Critical for the
    // unmount-during-fetch / remount-fires-new-fetch case where two
    // useAutoRefresh instances would otherwise race to write to the
    // store. With dedup, both get the same resolution and both write
    // the same result. They cannot disagree.
    if (inFlight) return inFlight;

    inFlight = (async (): Promise<LiveBtcSnapshot> => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        let res: Response;
        try {
          res = await fetch(COINGECKO_URL, {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timer);
        }
        if (!res.ok) {
          throw new Error(`CoinGecko HTTP ${res.status}`);
        }
        const data = (await res.json()) as CoinGeckoResponse;
        const price = data.market_data?.current_price?.usd;
        const ath = data.market_data?.ath?.usd;
        if (typeof price !== 'number' || typeof ath !== 'number') {
          throw new Error('CoinGecko payload missing market_data.current_price.usd or market_data.ath.usd');
        }
        const snap: LiveBtcSnapshot = {
          priceUsd: new Decimal(price),
          athUsd: new Decimal(ath),
          fetchedAt: new Date(),
          source: 'coingecko',
        };
        cachedSnapshot = snap;
        cachedAt = Date.now();
        return snap;
      } finally {
        // Always clear inFlight, success or failure, so the next call
        // gets a fresh chance. Successful results are also cached above
        // so subsequent calls within TTL skip the fetch entirely.
        // Failures do NOT update the cache so the previous successful
        // snapshot remains the last known good (and gets returned on
        // the next call within TTL of the LAST success, if any).
        inFlight = null;
      }
    })();

    return inFlight;
  }
  async fetchAaveUsdcBorrowApr(): Promise<LiveAaveRateSnapshot> {
    throw new Error('Aave rate not available via CoinGecko - wire DefiLlama in M4.');
  }
}
